import { VStack } from "@/components/ui/vstack";
import { View } from "@/components/ui/view";
import { ScrollView } from "react-native-gesture-handler";
import i18n from "i18n-js";
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FloatingLabelInput from "@/components/basic/FloatingLabelInput";
import Footer from "@/components/basic/Footer";
import ModalConfirm from '@/components/basic/ModalConfirm';
import PressableElement from "@/components/basic/PressableElement";
import AvatarEdit from "@/components/media/AvatarEdit";
import useIsMounted from '@/hooks/mounted';
import { backendRequest, saveImageAsync } from "@/stores/thunks";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { switchScreen } from '@/hooks/navigate';
import SwitchableElement from "@/components/basic/SwitchableElement";

export let updateContactChilds

export default function Contact({ navigation, route }) {
    const { contact, isEdit } = route.params

    const mounted = useIsMounted()
    const insets = useSafeAreaInsets()
    const dispatch = useDispatch()
    const [showModal, setShowModal] = useState(false)
    const [deleteAccount, setDeleteAccount] = useState(false)

    const server = useSelector((state) => state.server)
    const contacts = useSelector((state) => state.contacts)
    const workflows = useSelector((state) => state.workflows)

    const [wasEdit, setWasEdit] = useState(false)
    const [alias, setAlias] = useState(contact.contactAlias || contact.toUser.mail)
    const [image, setImage] = useState(contact?.contactImage?.id || contact.toUser.id)
    const [planningAllowed, setPlanningAllowed] = useState(contact.planningAllowed)
    const [sharedTo, setSharedTo] = useState(contact.workflows.filter(taskid => workflows[taskid] && !workflows[taskid].deleted))
    const [actChannels, setActChannels] = useState(contacts.channels.filter(channel => channel.contacts.includes(contact.id)).map(channel => channel.id))
    const [sharedFrom, setSharedFrom] = useState(contacts.workflows.filter(workflow => contact.id == workflow.contact))

    const selected = async (uri) => {
        setImage(uri)
    }

    const actions = [
        isEdit ? { icon: '' } : { icon: 'keyboard-arrow-left', text: 'footer.back', component: 'BACK' },
        { icon: 'home', text: 'footer.home', component: 'Home' },
        isEdit ? { icon: 'check', text: 'footer.finish', component: 'Contact', params: { contact: contact, isEdit: false } } : (server.online ? { icon: 'edit', text: 'footer.edit', component: 'Contact', params: { contact: contact, isEdit: true } } : { icon: '' })
    ]

    updateContactChilds = (channels, workflows) => {
        if (channels) {
            setActChannels(channels)
            // console.log('CHANNELS', contacts)
        } else {
            setSharedTo(workflows)
            // console.log('WORKFLOWS', workflows)
        }
    }

    useEffect(() => {
        navigation.setOptions({ title: i18n.t(isEdit ? 'contact.update' : 'contact.title'), info: alias })
    }, [alias, isEdit])

    function checkAndSave() {
        if (server.online) {
            if (deleteAccount) {
                console.log('DELETE')
                dispatch(backendRequest('DELETE_CONTACT_INLINE', '/contact/' + contact.id, null, 'DELETE', false))
            } else {
                const newImage = image == contact.toUser.id ? null : image
                const newAlias = alias == contact.toUser.mail ? null : alias.trim()
                if (null != newImage && typeof newImage != 'number' && newImage.startsWith('file:///')) {
                    dispatch(saveImageAsync('UPDATE_CONTACT_INLINE', '/contact/' + contact.id + '/image', { uri: newImage, title: contact.toUser.preferredName, description: alias }))
                }
                const addWorkflows = sharedTo.filter(workflow => !contact.workflows.includes(workflow))
                const delWorkflows = contact.workflows.filter(workflow => !sharedTo.includes(workflow))

                const oldChannels = contacts.channels.filter(channel => channel.contacts.includes(contact.id)).map(channel => channel.id);
                const addChannels = actChannels.filter(channel => !oldChannels.includes(channel))
                const delChannels = oldChannels.filter(channel => !actChannels.includes(channel))
                if (newAlias != contact.contactAlias || addWorkflows.length + delWorkflows.length + addChannels.length + delChannels.length > 0 || planningAllowed != contact.planningAllowed) {
                    dispatch(backendRequest('UPDATE_CONTACT_INLINE', '/contact/' + contact.id, {
                        contactAlias: newAlias,
                        planningAllowed: planningAllowed,
                        workflows: sharedTo.map(id => workflows[id].workflowId),
                        addChannels: addChannels,
                        delChannels: delChannels
                    }, 'PUT', false))
                }
            }
        }
    }

    useEffect(() => {
        if (wasEdit) {
            checkAndSave()
        }
        setWasEdit(isEdit)
    }, [isEdit])

    useEffect(() => {
        return () => {
            if (!mounted()) {
                checkAndSave()
            }
        };
    }, [mounted])

    const closeModal = (confirmed) => {
        setShowModal(false)
        if (confirmed) {
            setDeleteAccount(true)
            switchScreen(navigation, 'BACK')
        }
    }

    const showChannels = contacts.channels?.length > 0 && (isEdit || actChannels.length > 0)

    return (
        <View className="flex-1 bg-secondary-50">
            <ScrollView scrollIndicatorInsets={{ right: 1 }} contentContainerStyle={{ paddingBottom: 90 + insets.bottom }} className="flex-1 bg-secondary-50 p-3">
                <VStack space='xl' className="flex-1 px-2 py-3">
                    <AvatarEdit key={image} uri={image} selected={selected} sync={'/contact/media/' + image} isEdit={isEdit} />
                    <FloatingLabelInput h='h-[50px]' label={i18n.t('contact.alias')} value={alias} placeholder={i18n.t('contact.alias')} onChangeText={setAlias} isDisabled={!isEdit} />
                    <FloatingLabelInput h='h-[50px]' label={i18n.t('contact.preferredName')} value={contact.toUser.preferredName} placeholder={i18n.t('contact.preferredName')} isDisabled />
                    <PressableElement header={i18n.t('contact.workflowsApp', { num: (sharedTo?.length + (isEdit ? 0 : sharedFrom?.length)) || 0 })} mainText={i18n.t(isEdit ? 'contact.edit' : 'contact.show')} pressed={() => switchScreen(navigation, 'SharedWorkflows', { title: i18n.t('contact.workflows'), info: alias, contact: contact, sharedTo: sharedTo, sharedFrom: sharedFrom, isEdit: isEdit })} />
                    <PressableElement color={showChannels ? 'text-secondary-900' : 'text-secondary-400'} header={i18n.t('contact.channelsN', { num: actChannels.length })} mainText={i18n.t(isEdit ? 'contact.edit' : 'contact.show')} pressed={() => showChannels && switchScreen(navigation, 'Channels', { channels: actChannels, info: alias, isEdit: isEdit, isFinal: true })} />
                    <SwitchableElement header={i18n.t('contact.planAllowed')} mainText={i18n.t('contact.permit')} isChecked={planningAllowed} disabled={!isEdit} onToggle={() => setPlanningAllowed(!planningAllowed)} />
                    {contact.planningForAllowed && <PressableElement header={i18n.t('contact.planner')} mainText={i18n.t(isEdit ? 'contact.edit' : 'contact.show')} pressed={() => switchScreen(navigation, 'Planner', { isEdit: isEdit, contact: contact })} />}
                    {isEdit && <PressableElement header={i18n.t('contact.delete')} mainText={i18n.t('contact.deleteContact')} color='text-error-600' pressed={() => setShowModal(true)} />}
                </VStack>
            </ScrollView>
            <ModalConfirm showModal={showModal && server.online} closeModal={closeModal} title='contact.deleteContact' text='contact.deleteContactText' options={{ user: contact.contactAlias || contact.toUser.mail }} />
            <Footer navigation={navigation} actions={actions} />
        </View>
    );
}
